// Copyright (c) 2023. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { Buffer } from 'buffer';
import { explainRegularObject, isRegularObject } from "../../../../io/hyperify/core/types/RegularObject";
import { explainNoOtherKeysInDevelopment, hasNoOtherKeysInDevelopment } from "../../../../io/hyperify/core/types/OtherKeys";
import { explainString, isStringOrUndefined } from "../../../../io/hyperify/core/types/String";
import { explain, explainNot, explainOk, explainOr, explainProperty } from "../../../../io/hyperify/core/types/explain";
import { isUndefined } from "../../../../io/hyperify/core/types/undefined";
import { parseJson } from "../../../../io/hyperify/core/Json";

export interface SendanorSeoImageData {
    readonly title ?: string;
    readonly description ?: string;
    readonly price ?: string;
    readonly buttonText ?: string;
    readonly discountText ?: string;
    readonly extraInfo ?: string;
    readonly tagLine ?: string;
}

export function createSendanorSeoImageData (
    title        : string | undefined,
    description  : string | undefined,
    price        : string | undefined,
    buttonText   : string | undefined,
    discountText : string | undefined,
    extraInfo    : string | undefined,
    tagLine      : string | undefined,
) : SendanorSeoImageData {
    return {
        ...(title !== undefined ? {title} : {}),
        ...(description !== undefined ? {description} : {}),
        ...(price !== undefined ? {price} : {}),
        ...(buttonText !== undefined ? {buttonText} : {}),
        ...(discountText !== undefined ? {discountText} : {}),
        ...(extraInfo !== undefined ? {extraInfo} : {}),
        ...(tagLine !== undefined ? {tagLine} : {}),
    };
}

export function isSendanorSeoImageData (value: unknown) : value is SendanorSeoImageData {
    return (
        isRegularObject(value)
        && hasNoOtherKeysInDevelopment(value, [
            'title',
            'description',
            'price',
            'buttonText',
            'discountText',
            'extraInfo',
            'tagLine'
        ])
        && isStringOrUndefined(value?.title)
        && isStringOrUndefined(value?.description)
        && isStringOrUndefined(value?.price)
        && isStringOrUndefined(value?.buttonText)
        && isStringOrUndefined(value?.discountText)
        && isStringOrUndefined(value?.extraInfo)
        && isStringOrUndefined(value?.tagLine)
    );
}

export function explainSendanorSeoImageData (value: any) : string {
    return explain(
        [
            explainRegularObject(value),
            explainNoOtherKeysInDevelopment(value, [
                'title',
                'description',
                'price',
                'buttonText',
                'discountText',
                'extraInfo',
                'tagLine'
            ])
            , explainProperty("title", explainString(value?.title))
            , explainProperty("description", explainString(value?.description))
            , explainProperty("price", explainString(value?.price))
            , explainProperty("buttonText", explainString(value?.buttonText))
            , explainProperty("discountText", explainString(value?.discountText))
            , explainProperty("extraInfo", explainString(value?.extraInfo))
            , explainProperty("tagLine", explainString(value?.tagLine))
        ]
    );
}

export function parseSendanorSeoImageData (value: unknown) : SendanorSeoImageData | undefined {
    if (isSendanorSeoImageData(value)) return value;
    return undefined;
}

export function isSendanorSeoImageDataOrUndefined (value: unknown): value is SendanorSeoImageData | undefined {
    return isUndefined(value) || isSendanorSeoImageData(value);
}

export function explainSendanorSeoImageDataOrUndefined (value: unknown): string {
    return isSendanorSeoImageDataOrUndefined(value) ? explainOk() : explainNot(explainOr(['SendanorSeoImageData', 'undefined']));
}

export function decodeSendanorSeoImageData (value : string) : SendanorSeoImageData {
    const b = Buffer.from(value, 'base64');
    const t = b.toString('utf8');
    const json : any = parseJson(t);
    const title = json?.title ?? '';
    const description = json?.description ?? '';
    const price = json?.price;
    const buttonText = json?.buttonText;
    const discountText = json?.discountText;
    const extraInfo = json?.extraInfo;
    const tagLine = json?.tagLine;
    return createSendanorSeoImageData(
        title,
        description,
        price,
        buttonText,
        discountText,
        extraInfo,
        tagLine,
    );
}

export function encodeSendanorSeoImageData (value: SendanorSeoImageData) : string {
    return Buffer.from( JSON.stringify(value), 'utf8' ).toString('base64');
}
